import React, { useState } from 'react'
import { PageHeader } from '@/components/layout'
import { useMyAffiliate, useAffiliateStats, useAffiliateClicks } from '@/hooks/useAffiliate'

type FilterType = 'all' | 'converted' | 'not_converted'

export const AffiliateTrackingPage: React.FC = () => {
    const [filter, setFilter] = useState<FilterType>('all')
    const [dateRange, setDateRange] = useState('7d')

    // Fetch data from API
    const { data: affiliate, isLoading: loadingAffiliate } = useMyAffiliate('')
    const { data: stats, isLoading: loadingStats } = useAffiliateStats(affiliate?.id || '')
    const { data: clicks, isLoading: loadingClicks } = useAffiliateClicks(affiliate?.id || '')

    const isLoading = loadingAffiliate || loadingStats || loadingClicks

    const filteredClicks = (clicks || []).filter((click: any) => {
        if (filter === 'all') return true
        if (filter === 'converted') return click.converted
        return !click.converted
    })

    const totalClicks = stats?.totalClicks || 0
    const conversions = stats?.totalConversions || 0
    const conversionRate = stats?.conversionRate?.toFixed(1) || '0'
    const totalEarnings = stats?.totalEarnings || 0

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        )
    }

    return (
        <div>
            <PageHeader
                title="Performance Tracking"
                subtitle="Monitor your affiliate link performance"
            />

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="material-symbols-outlined text-blue-400 text-[20px]">ads_click</span>
                        <p className="text-gray-500 dark:text-text-muted text-xs font-medium uppercase">Total Clicks</p>
                    </div>
                    <p className="text-gray-900 dark:text-white text-2xl font-bold">{totalClicks}</p>
                </div>
                <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="material-symbols-outlined text-green-400 text-[20px]">check_circle</span>
                        <p className="text-gray-500 dark:text-text-muted text-xs font-medium uppercase">Conversions</p>
                    </div>
                    <p className="text-gray-900 dark:text-white text-2xl font-bold">{conversions}</p>
                </div>
                <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="material-symbols-outlined text-primary text-[20px]">percent</span>
                        <p className="text-gray-500 dark:text-text-muted text-xs font-medium uppercase">Conv. Rate</p>
                    </div>
                    <p className="text-gray-900 dark:text-white text-2xl font-bold">{conversionRate}%</p>
                </div>
                <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="material-symbols-outlined text-purple-400 text-[20px]">trending_up</span>
                        <p className="text-gray-500 dark:text-text-muted text-xs font-medium uppercase">Total Earnings</p>
                    </div>
                    <p className="text-gray-900 dark:text-white text-2xl font-bold">Rp {totalEarnings.toLocaleString('id-ID')}</p>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-2">
                    {(['all', 'converted', 'not_converted'] as FilterType[]).map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`px-4 py-2 rounded-lg text-xs font-bold uppercase transition-colors ${filter === f
                                    ? 'bg-primary text-white'
                                    : 'bg-white dark:bg-surface-card text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-white/5'
                                }`}
                        >
                            {f === 'all' ? 'All' : f === 'converted' ? 'Converted' : 'Not Converted'}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    {['7d', '30d', '90d', 'all'].map((range) => (
                        <button
                            key={range}
                            onClick={() => setDateRange(range)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${dateRange === range
                                    ? 'bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white'
                                    : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'
                                }`}
                        >
                            {range === 'all' ? 'All Time' : `Last ${range}`}
                        </button>
                    ))}
                </div>
            </div>

            {/* Click History */}
            <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-gray-200 dark:border-white/5">
                            <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Timestamp</th>
                            <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Course</th>
                            <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-white/5">
                        {!clicks || clicks.length === 0 ? (
                            <tr>
                                <td colSpan={3} className="px-5 py-12 text-center">
                                    <div className="text-gray-500 dark:text-text-muted">
                                        <p className="mb-2">No click tracking data available yet.</p>
                                        <p className="text-xs">Click data will appear here once your affiliate links are used.</p>
                                    </div>
                                </td>
                            </tr>
                        ) : filteredClicks.length === 0 ? (
                            <tr>
                                <td colSpan={3} className="px-5 py-12 text-center text-gray-500 dark:text-text-muted">No clicks found for the selected filter</td>
                            </tr>
                        ) : (
                            filteredClicks.map((click: any) => (
                                <tr key={click.id} className="hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
                                    <td className="px-5 py-4 text-sm text-gray-500 dark:text-text-muted">
                                        {new Date(click.clickedAt).toLocaleString('en-US', {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </td>
                                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white">{click.courseName || click.landingPage}</td>
                                    <td className="px-5 py-4">
                                        <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${click.converted
                                                ? 'bg-green-500/20 text-green-400'
                                                : 'bg-gray-500/20 text-gray-500 dark:text-gray-400'
                                            }`}>
                                            {click.converted ? 'Converted' : 'Click Only'}
                                        </span>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default AffiliateTrackingPage
